import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  BookOpen, DollarSign, School, Truck, AlertTriangle, ArrowRight, 
  FileText, Receipt, CheckCircle2, Clock
} from 'lucide-react';
import { Link } from 'react-router-dom';

export default function CivicaDashboard() {
  return (
    <div className="space-y-8 animate-fade-in">
      {/* Alert Banner */}
      <Card className="bg-red-50 border-red-200">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <p className="text-sm text-red-800 font-medium">
              3 replacement curated titles due TODAY. Penalty risk if missed.
            </p>
            <Button asChild size="sm" variant="destructive" className="ml-auto">
              <Link to="/civica/curation">Go to Curation →</Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* KPI Tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <BookOpen className="w-5 h-5 text-[#2E5F8A]" />
              <span className="text-sm text-slate-500">Curated Titles</span>
            </div>
            <div className="text-2xl font-bold text-slate-900">228</div>
            <div className="text-xs text-slate-500 mt-1">
              194 approved | 34 pending | 3 rejected
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <DollarSign className="w-5 h-5 text-[#2E5F8A]" />
              <span className="text-sm text-slate-500">Supplier Quotes</span>
            </div>
            <div className="text-2xl font-bold text-slate-900">156</div>
            <div className="text-xs text-slate-500 mt-1">
              MOE-approved | 38 pending | 3 queries
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <School className="w-5 h-5 text-[#2E5F8A]" />
              <span className="text-sm text-slate-500">Acquisition Module</span>
            </div>
            <div className="text-2xl font-bold text-emerald-600">OPEN</div>
            <div className="text-xs text-slate-500 mt-1">
              248/287 schools | 39 outstanding | 5 days left
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <Truck className="w-5 h-5 text-[#2E5F8A]" />
              <span className="text-sm text-slate-500">Delivery</span>
            </div>
            <div className="text-2xl font-bold text-amber-600">In Transit</div>
            <div className="text-xs text-slate-500 mt-1">
              Pilot C2 Nov batch dispatched
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <Receipt className="w-5 h-5 text-[#2E5F8A]" />
              <span className="text-sm text-slate-500">Invoicing</span>
            </div>
            <div className="text-2xl font-bold text-slate-900">4</div>
            <div className="text-xs text-slate-500 mt-1">
              Invoices pending | $49,680 outstanding
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Task Board */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Task Board</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* TODO */}
            <div className="bg-red-50 rounded-lg p-4">
              <h3 className="font-semibold text-red-800 mb-3">TODO</h3>
              <div className="space-y-2">
                <div className="bg-white p-3 rounded border border-red-200 text-sm">
                  Replace 3 rejected titles (due today!)
                </div>
                <div className="bg-white p-3 rounded border text-sm">
                  Submit 38 quotes to MOE
                </div>
              </div>
            </div>

            {/* IN PROGRESS */}
            <div className="bg-amber-50 rounded-lg p-4">
              <h3 className="font-semibold text-amber-800 mb-3">IN PROGRESS</h3>
              <div className="space-y-2">
                <div className="bg-white p-3 rounded border text-sm">
                  MOE HQ reviewing 34 titles
                </div>
                <div className="bg-white p-3 rounded border text-sm">
                  Nov pilot delivery in transit
                </div>
              </div>
            </div>

            {/* AWAITING */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-semibold text-blue-800 mb-3">AWAITING</h3>
              <div className="space-y-2">
                <div className="bg-white p-3 rounded border text-sm">
                  MOE HQ approval on 38 quotes
                </div>
                <div className="bg-white p-3 rounded border text-sm">
                  39 schools to complete selections
                </div>
              </div>
            </div>

            {/* DONE */}
            <div className="bg-emerald-50 rounded-lg p-4">
              <h3 className="font-semibold text-emerald-800 mb-3">DONE</h3>
              <div className="space-y-2">
                <div className="bg-white p-3 rounded border text-sm">
                  194 titles approved ✓
                </div>
                <div className="bg-white p-3 rounded border text-sm">
                  156 quotes approved ✓
                </div>
                <div className="bg-white p-3 rounded border text-sm">
                  Pilot Cycle 1 complete ✓
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="hover:shadow-md transition-shadow cursor-pointer">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">Curation Management</h3>
                <p className="text-sm text-slate-500">Submit replacements, manage titles</p>
              </div>
              <Button asChild size="sm" variant="outline">
                <Link to="/civica/curation">
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">Quote Invitations</h3>
                <p className="text-sm text-slate-500">Invite suppliers, compare quotes</p>
              </div>
              <Button asChild size="sm" variant="outline">
                <Link to="/civica/quote-invitations">
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">Delivery Status</h3>
                <p className="text-sm text-slate-500">Track and update dispatches</p>
              </div>
              <Button asChild size="sm" variant="outline">
                <Link to="/civica/delivery-status">
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
